import { authFetch, apiUrl } from "../utils/api";

/**
 * Incremental parser for the JSON `data:` frames emitted by /api/chat.
 * Transport framing is kept separate from chat state so it can be tested
 * without React or a live ReadableStream.
 */
export function createChatSSEParser({ onEvent, onParseError } = {}) {
  let buffer = "";

  const parseFrame = (frame) => {
    const dataLines = frame
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data: "))
      .map((line) => line.slice(6));

    if (dataLines.length === 0) return;

    try {
      onEvent?.(JSON.parse(dataLines.join("\n")));
    } catch (error) {
      onParseError?.(error);
    }
  };

  return {
    push(chunk) {
      buffer += chunk;
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() || "";
      frames.forEach(parseFrame);
    },

    finish() {
      // A well-formed SSE frame ends with a blank line. Tolerate a final
      // unterminated data frame so a clean connection close cannot lose it.
      if (buffer.trim()) parseFrame(buffer);
      buffer = "";
    },
  };
}

export async function readChatSSE(body, { onEvent, onParseError } = {}) {
  if (!body?.getReader) {
    throw new Error("Chat response did not include a readable stream");
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createChatSSEParser({ onEvent, onParseError });

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }

  parser.push(decoder.decode());
  parser.finish();
}

export function postChat(requestBody, signal) {
  return authFetch(apiUrl("/api/chat"), {
    method: "POST",
    signal,
    body: JSON.stringify(requestBody),
  });
}

export function cancelChat(conversationId) {
  return authFetch(apiUrl("/api/chat/cancel"), {
    method: "POST",
    body: JSON.stringify({ conversationId }),
  });
}
